import { Control, Controller } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NoxusInput } from "@/components/Form/InputField";
import {
  Plus,
  Trash,
  GripVertical,
  ChevronUp,
  Copy,
  X,
  Text,
  Hash,
  ToggleRight,
  Calendar,
  AlignLeft,
  ListFilter,
  Table as TableIcon,
  Paperclip,
} from "lucide-react";
import { TableColumnsEditor, type TableColumn } from "@/components/Fields";
import { CSS } from "@dnd-kit/utilities";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  CollisionDetection,
} from "@dnd-kit/core";
import {
  arrayMove,
  useSortable,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { Button } from "@/components/shadcn/button";
import { SpotSelect } from "@/components/Form/SelectField";
import { Badge } from "@/components/shadcn/badge";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Switch } from "@/components/shadcn/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/shadcn/dropdown-menu";
import { cn } from "@/lib/utils";
import { TaskLayoutDesigner } from "@/components/Spotflow/ConfigDrawer/Configs/TaskLayoutDesigner";

export interface ExtractionField {
  // Stable row identity for layout references; generated for new rows.
  // Older saved schemas have none — consumers fall back to Label.
  id?: string;
  Label: string;
  // Legacy machine identifier auto-derived from Label. Newer entries are
  // saved without it; the runtime treats Label as the canonical identifier
  // for the extraction connector keys, LLM output, and downstream consumers.
  // Kept optional so older saved schemas still parse.
  Name?: string;
  type: string;
  // list[str] for select, list[TableColumn] for table, undefined otherwise.
  options?: string[] | TableColumn[];
  // When set, the row is linked to an inbox field — value commits to
  // case.data[field_id] on task completion. `Type` mirrors the inbox
  // field's type and is locked.
  field_id?: string;
  // Approval-only: when explicitly false, the form renders the linked
  // value read-only ("approve as-is"). Extraction ignores this field.
  editable?: boolean;
}

export interface AvailableFieldOption {
  field_id: string;
  label: string;
  name: string;
  type: string;
  group?: string | null;
  // list[str] for select fields, TableColumn[] for table fields.
  options?: string[] | TableColumn[] | null;
}

const inboxTypeToExtractionType = (inboxType: string): string => {
  switch (inboxType) {
    case "text":
    case "email":
    case "url":
      return "text";
    case "textarea":
      return "textarea";
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "date":
      return "date";
    case "select":
    case "multi_select":
      return "select";
    case "table":
      return "table";
    case "file":
      return "file";
    default:
      return "text";
  }
};

const INBOX_TYPE_LABELS: Record<string, string> = {
  text: "Short text",
  textarea: "Long text",
  number: "Number",
  date: "Date",
  boolean: "Yes / No",
  select: "Select",
  multi_select: "Multi select",
  email: "Email",
  phone: "Phone",
  url: "URL",
  file: "File",
  table: "Table",
};

const TYPE_OPTIONS = [
  "text",
  "textarea",
  "number",
  "date",
  "boolean",
  "select",
  "table",
];

const getTypeIcon = (type: string) => {
  switch (type) {
    case "text":
      return <Text className="w-4 h-4" />;
    case "textarea":
      return <AlignLeft className="w-4 h-4" />;
    case "number":
      return <Hash className="w-4 h-4" />;
    case "date":
      return <Calendar className="w-4 h-4" />;
    case "boolean":
      return <ToggleRight className="w-4 h-4" />;
    case "select":
      return <ListFilter className="w-4 h-4" />;
    case "table":
      return <TableIcon className="w-4 h-4" />;
    case "file":
      return <Paperclip className="w-4 h-4" />;
    default:
      return <Text className="w-4 h-4" />;
  }
};

const resolveType = (type: any): string => {
  if (typeof type === "object" && type?.value) return type.value;
  return typeof type === "string" ? type : "text";
};

interface OptionsEditorProps {
  options: string[];
  onChange: (options: string[]) => void;
  isDisabled?: boolean;
}

const OptionsEditor = ({
  options,
  onChange,
  isDisabled,
}: OptionsEditorProps) => {
  const [newOption, setNewOption] = useState("");

  const handleAdd = () => {
    const trimmed = newOption.trim();
    if (trimmed && !options.includes(trimmed)) {
      onChange([...options, trimmed]);
      setNewOption("");
    }
  };

  const handleRemove = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium">Options</label>
      {options.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {options.map((opt, i) => (
            <Badge
              key={i}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              {opt}
              {!isDisabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="ml-0.5 hover:bg-muted rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <NoxusInput
          value={newOption}
          onChange={(e) => setNewOption(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type an option and press Enter"
          disable={isDisabled}
          customStyle="bg-background"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          disabled={isDisabled || !newOption.trim()}
          className="shrink-0"
        >
          Add
        </Button>
      </div>
    </div>
  );
};

interface AccordionFieldProps {
  id: string;
  value: string;
  entry: ExtractionField;
  entryIndex: number;
  isOpen: boolean;
  isDisabled?: boolean;
  cantDelete: boolean;
  hasDuplicateLabel: boolean;
  typeOptions: string[];
  availableFields?: AvailableFieldOption[];
  forApproval?: boolean;
  allowLinking?: boolean;
  inGroup?: boolean;
  onCopy: (entry: ExtractionField) => void;
  onDelete: (index: number) => void;
  onChange: (index: number, field: Partial<ExtractionField>) => void;
}

const AccordionField = ({
  id,
  value,
  entry,
  entryIndex,
  isOpen,
  isDisabled,
  cantDelete,
  hasDuplicateLabel,
  typeOptions,
  availableFields,
  forApproval,
  allowLinking,
  inGroup,
  onCopy,
  onDelete,
  onChange,
}: AccordionFieldProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id, disabled: isDisabled });

  // Linked rows take their type and options from the case property (the
  // pickers/editors are locked for them) — derive both so they can't
  // drift, and heal stale stored values.
  const linkedField =
    allowLinking && entry.field_id
      ? (availableFields ?? []).find((f) => f.field_id === entry.field_id)
      : undefined;
  const effectiveType = linkedField
    ? inboxTypeToExtractionType(linkedField.type)
    : resolveType(entry.type);
  const linkedOptions =
    linkedField && (effectiveType === "select" || effectiveType === "table")
      ? (linkedField.options ?? undefined)
      : undefined;
  const entryType = resolveType(entry.type);
  const serializedEntryOptions = JSON.stringify(entry.options ?? null);
  const serializedLinkedOptions = JSON.stringify(linkedOptions ?? null);
  useEffect(() => {
    if (isDisabled || !linkedField) return;
    if (
      entryType === effectiveType &&
      serializedEntryOptions === serializedLinkedOptions
    )
      return;
    onChange(entryIndex, { type: effectiveType, options: linkedOptions });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    linkedField?.field_id,
    effectiveType,
    isDisabled,
    entryType,
    serializedEntryOptions,
    serializedLinkedOptions,
  ]);

  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <AccordionItem
        value={value}
        className={cn(
          "group border rounded-md px-4 last:border-b first:border-t",
          inGroup ? "bg-background" : "bg-[#FCFCFC]"
        )}
      >
        <div className="flex items-center gap-2 [&>h3]:grow">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
          </div>
          <AccordionTrigger
            className="hover:no-underline py-3 grow items-center overflow-visible"
            hideChevron
          >
            <div className="flex items-center gap-2 w-full pr-2">
              <span className="text-sm font-medium">
                {entry.Label || `Field ${entryIndex + 1}`}
              </span>
              {allowLinking && entry.field_id && (
                <Badge variant="color-blue" isCompact>
                  Linked
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <div className="h-6 flex items-center">
                <Badge
                  variant="outline"
                  className="group-hover:hidden capitalize"
                >
                  {effectiveType}
                </Badge>
              </div>
              <div className="hidden group-hover:flex items-center gap-2 h-6">
                <NoxusTooltip content="Duplicate field">
                  <Button
                    variant="ghost"
                    size="iconsm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopy(entry);
                    }}
                    isDisabled={isDisabled}
                  >
                    <Copy className="size-4" strokeWidth={1.25} />
                  </Button>
                </NoxusTooltip>
                <NoxusTooltip
                  content={
                    cantDelete
                      ? "At least one field is required"
                      : "Delete field"
                  }
                >
                  <Button
                    variant="ghost"
                    size="iconsm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(entryIndex);
                    }}
                    isDisabled={cantDelete || isDisabled}
                  >
                    <Trash className="size-4" strokeWidth={1.25} />
                  </Button>
                </NoxusTooltip>
              </div>
              <Button variant="ghost" size="iconsm">
                <ChevronUp
                  className={cn(
                    "transition-transform duration-200",
                    !isOpen ? "rotate-180" : ""
                  )}
                />
              </Button>
            </div>
          </AccordionTrigger>
        </div>

        <AccordionContent className="pb-4 pt-2 border-t">
          <div className="grid grid-cols-12 gap-3 p-0.5">
            <div className="flex flex-col gap-1.5 col-span-12">
              <label className="text-sm font-medium">Label</label>
              <NoxusInput
                value={entry.Label || ""}
                customStyle="bg-background"
                onChange={(e) =>
                  onChange(entryIndex, { Label: e.target.value })
                }
                placeholder="Field Label"
                disable={isDisabled}
                hasError={hasDuplicateLabel}
              />
              {hasDuplicateLabel && (
                <span className="text-xs text-destructive">
                  Field labels must be unique
                </span>
              )}
            </div>
            {!(allowLinking && entry.field_id) && (
              <div className="flex flex-col gap-1.5 col-span-12">
                <label className="text-sm font-medium">Type</label>
                <SpotSelect
                  name={`type_${entryIndex}`}
                  value={resolveType(entry.type)}
                  onChange={(opt: { value: string; label: string } | null) => {
                    // SpotSelect hands back the option object, not a bare
                    // string. Older code typed this as `string` and stored
                    // the whole `{label, value}` blob as `type`, which broke
                    // every consumer that expected a string.
                    const value = opt?.value ?? "text";
                    onChange(entryIndex, {
                      type: value,
                      // Only select and table carry `options` payloads.
                      ...(value !== "select" && value !== "table"
                        ? { options: undefined }
                        : {}),
                    });
                  }}
                  options={typeOptions.map((opt) => ({
                    value: opt,
                    label: opt.charAt(0).toUpperCase() + opt.slice(1),
                  }))}
                  isDisabled={isDisabled}
                  customItemContent={(option) => (
                    <div className="flex items-center gap-2">
                      {option?.value && getTypeIcon(option.value)}
                      <span>{option?.label}</span>
                    </div>
                  )}
                />
              </div>
            )}
            {effectiveType === "select" && (
              <div className="col-span-12">
                <OptionsEditor
                  options={(entry.options as string[]) || []}
                  onChange={(options) => onChange(entryIndex, { options })}
                  isDisabled={isDisabled || Boolean(linkedField)}
                />
              </div>
            )}
            {effectiveType === "table" && (
              <div className="col-span-12">
                <TableColumnsEditor
                  value={(entry.options as TableColumn[]) || []}
                  onChange={(options) => onChange(entryIndex, { options })}
                  isDisabled={isDisabled || Boolean(linkedField)}
                />
              </div>
            )}
            {allowLinking && availableFields && availableFields.length > 0 && (
              <div className="flex flex-col gap-1.5 col-span-12">
                <label className="text-sm font-medium">
                  Link to case property
                </label>
                <SpotSelect
                  name={`bind_${entryIndex}`}
                  value={entry.field_id || ""}
                  onChange={(opt: { value: string; label: string } | null) => {
                    const fieldId = opt?.value || "";
                    if (!fieldId) {
                      onChange(entryIndex, { field_id: undefined });
                      return;
                    }
                    const picked = availableFields.find(
                      (f) => f.field_id === fieldId
                    );
                    if (!picked) return;
                    const mappedType = inboxTypeToExtractionType(picked.type);
                    onChange(entryIndex, {
                      field_id: picked.field_id,
                      Label: picked.label,
                      type: mappedType,
                      options:
                        mappedType === "select" || mappedType === "table"
                          ? (picked.options ?? undefined)
                          : undefined,
                    });
                  }}
                  options={[
                    { value: "", label: "Free-form (no link)" },
                    ...availableFields.map((f) => ({
                      value: f.field_id,
                      label: f.group ? `${f.label} (${f.group})` : f.label,
                    })),
                  ]}
                  customItemContent={(opt) => {
                    const linkedField = availableFields.find(
                      (f) => f.field_id === opt?.value
                    );
                    return (
                      <div className="flex w-full min-w-0 items-center justify-between gap-2">
                        <span className="truncate text-sm">{opt?.label}</span>
                        {linkedField && (
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {INBOX_TYPE_LABELS[linkedField.type] ??
                              linkedField.type}
                          </span>
                        )}
                      </div>
                    );
                  }}
                  isDisabled={isDisabled}
                />
              </div>
            )}
            {allowLinking && entry.field_id && forApproval && (
              <div className="flex items-center justify-between gap-3 col-span-12">
                <div className="flex flex-col gap-0.5">
                  <label className="text-sm font-medium">Allow edits</label>
                  <span className="text-xs text-muted-foreground">
                    Let the human edit the value before approving.
                  </span>
                </div>
                <Switch
                  checked={entry.editable !== false}
                  onCheckedChange={(c) => onChange(entryIndex, { editable: c })}
                  disabled={isDisabled}
                />
              </div>
            )}
          </div>
        </AccordionContent>
      </AccordionItem>
    </div>
  );
};

interface SchemaBucketsProps {
  value: ExtractionField[];
  onValueChange: (next: ExtractionField[]) => void;
  availableFields?: AvailableFieldOption[];
  forApproval?: boolean;
  allowLinking?: boolean;
  openItems: string[];
  setOpenItems: (items: string[]) => void;
  isDisabled?: boolean;
  cantDelete: boolean;
  duplicateLabelIndices: Set<number>;
  typeOptions: string[];
  onCopy: (entry: ExtractionField) => void;
  onDelete: (index: number) => void;
  onChange: (index: number, fields: Partial<ExtractionField>) => void;
}

interface RowWithIndex {
  row: ExtractionField;
  index: number;
}

const SchemaBuckets = ({
  value,
  onValueChange,
  availableFields,
  forApproval,
  allowLinking,
  openItems,
  setOpenItems,
  isDisabled,
  cantDelete,
  duplicateLabelIndices,
  typeOptions,
  onCopy,
  onDelete,
  onChange,
}: SchemaBucketsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [activeId, setActiveId] = useState<string | null>(null);
  const closedBucketsSnapshot = useRef<Set<string> | null>(null);

  const removeGroup = useCallback(
    (groupName: string) => {
      if (isDisabled) return;
      onValueChange(
        value.filter((row) => {
          if (!row.field_id) return true;
          const linked = (availableFields ?? []).find(
            (f) => f.field_id === row.field_id
          );
          return linked?.group !== groupName;
        })
      );
    },
    [value, onValueChange, availableFields, isDisabled]
  );
  const fieldsById = useMemo(() => {
    const m: Record<string, AvailableFieldOption> = {};
    for (const f of availableFields ?? []) m[f.field_id] = f;
    return m;
  }, [availableFields]);

  // Bucket rows by their linked field's group. When linking is disabled,
  // skip bucketing entirely — every row falls into "Other" and renders
  // as a flat list (legacy behaviour).
  const buckets = useMemo(() => {
    const named = new Map<string, RowWithIndex[]>();
    const other: RowWithIndex[] = [];
    value.forEach((row, index) => {
      const linkedGroup =
        allowLinking && row.field_id ? fieldsById[row.field_id]?.group : null;
      if (linkedGroup) {
        if (!named.has(linkedGroup)) named.set(linkedGroup, []);
        named.get(linkedGroup)!.push({ row, index });
      } else {
        other.push({ row, index });
      }
    });
    return { named, other };
  }, [value, fieldsById, allowLinking]);

  const [closedBuckets, setClosedBuckets] = useState<Set<string>>(new Set());
  const toggleBucket = (key: string) => {
    setClosedBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Each row index → bucket key so we know which rows are siblings.
  const bucketByIndex = useMemo(() => {
    const m = new Map<number, string>();
    for (const [name, rows] of buckets.named.entries()) {
      for (const { index } of rows) m.set(index, name);
    }
    for (const { index } of buckets.other) m.set(index, "__ungrouped__");
    return m;
  }, [buckets]);

  const handleDragStart = useCallback(
    (event: { active: { id: string | number } }) => {
      const id = String(event.active.id);
      setActiveId(id);
      setOpenItems([]);
      // Top-level drag (group reorder, or ungrouped row passing groups):
      // collapse every bucket for the duration of the drag so all items
      // are uniform height. Otherwise an expanded bucket distorts the
      // verticalListSortingStrategy translations as you drag past it.
      const isTopLevelDrag =
        id.startsWith("group:") ||
        (id.startsWith("row:") &&
          bucketByIndex.get(Number(id.replace(/^row:/, ""))) ===
            "__ungrouped__");
      if (isTopLevelDrag) {
        setClosedBuckets((prev) => {
          closedBucketsSnapshot.current = prev;
          return new Set(buckets.named.keys());
        });
      }
    },
    [setOpenItems, buckets, bucketByIndex]
  );

  const restoreBuckets = useCallback(() => {
    if (closedBucketsSnapshot.current !== null) {
      const restored = closedBucketsSnapshot.current;
      closedBucketsSnapshot.current = null;
      setClosedBuckets(restored);
    }
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    restoreBuckets();
  }, [restoreBuckets]);

  // Top-level draggable IDs: groups appear as `group:NAME` blocks and
  // ungrouped rows appear as `row:INDEX`. Together they're sortable
  // among each other, so a group can move past an ungrouped row.
  const topLevelIds = useMemo(() => {
    const ids: string[] = [];
    const seen = new Set<string>();
    value.forEach((row, index) => {
      const linked = row.field_id ? fieldsById[row.field_id] : undefined;
      const g = linked?.group;
      if (g) {
        if (!seen.has(g)) {
          seen.add(g);
          ids.push(`group:${g}`);
        }
      } else {
        ids.push(`row:${index}`);
      }
    });
    return ids;
  }, [value, fieldsById]);

  // Drop scope: which items can the active item land on?
  // - group block → top-level items (other groups + ungrouped rows)
  // - ungrouped row → top-level items
  // - row inside a named group → only other rows in that same group
  const isTopLevelId = (id: string) =>
    id.startsWith("group:") ||
    (id.startsWith("row:") &&
      bucketByIndex.get(Number(id.replace(/^row:/, ""))) === "__ungrouped__");

  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      const activeId = String(args.active.id);
      const activeIsTopLevel = isTopLevelId(activeId);
      let activeBucket: string | undefined;
      if (!activeIsTopLevel && activeId.startsWith("row:")) {
        activeBucket = bucketByIndex.get(Number(activeId.replace(/^row:/, "")));
      }
      const candidates = args.droppableContainers.filter((c) => {
        const cid = String(c.id);
        if (activeIsTopLevel) return isTopLevelId(cid);
        if (!cid.startsWith("row:")) return false;
        return (
          bucketByIndex.get(Number(cid.replace(/^row:/, ""))) === activeBucket
        );
      });
      return closestCenter({ ...args, droppableContainers: candidates });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bucketByIndex]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveId(null);
      restoreBuckets();
      const { active, over } = event;
      if (!over || isDisabled || active.id === over.id) return;

      const activeId = String(active.id);
      const overId = String(over.id);

      // Inner-bucket row drag: both ids are `row:N` for rows in the same
      // named group (collisionDetection has already filtered to siblings).
      if (
        activeId.startsWith("row:") &&
        overId.startsWith("row:") &&
        !isTopLevelId(activeId) &&
        !isTopLevelId(overId)
      ) {
        const fromIdx = Number(activeId.replace(/^row:/, ""));
        const toIdx = Number(overId.replace(/^row:/, ""));
        if (Number.isNaN(fromIdx) || Number.isNaN(toIdx)) return;
        if (bucketByIndex.get(fromIdx) !== bucketByIndex.get(toIdx)) return;
        onValueChange(arrayMove(value, fromIdx, toIdx));
        return;
      }

      // Top-level reorder: groups and ungrouped rows freely intermix.
      if (!isTopLevelId(activeId) || !isTopLevelId(overId)) return;
      const fromPos = topLevelIds.indexOf(activeId);
      const toPos = topLevelIds.indexOf(overId);
      if (fromPos < 0 || toPos < 0) return;
      const newOrder = arrayMove(topLevelIds, fromPos, toPos);

      // Rebuild the flat value array from the new top-level order.
      const next: ExtractionField[] = [];
      for (const id of newOrder) {
        if (id.startsWith("group:")) {
          const name = id.replace(/^group:/, "");
          for (const { row } of buckets.named.get(name) ?? []) next.push(row);
        } else {
          const idx = Number(id.replace(/^row:/, ""));
          if (!Number.isNaN(idx) && value[idx]) next.push(value[idx]);
        }
      }
      onValueChange(next);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      buckets,
      bucketByIndex,
      value,
      onValueChange,
      isDisabled,
      topLevelIds,
      restoreBuckets,
    ]
  );

  const renderRowsList = (rows: RowWithIndex[], wrapInContext: boolean) => {
    const itemIds = rows.map(({ index }) => `row:${index}`);
    const inner = (
      <Accordion
        type="multiple"
        value={openItems}
        onValueChange={setOpenItems}
        className="flex flex-col gap-3"
      >
        {rows.map(({ row, index }) => (
          <AccordionField
            key={index}
            id={`row:${index}`}
            value={index.toString()}
            entry={row}
            entryIndex={index}
            isOpen={openItems.includes(index.toString())}
            isDisabled={isDisabled}
            cantDelete={cantDelete}
            hasDuplicateLabel={duplicateLabelIndices.has(index)}
            typeOptions={typeOptions}
            availableFields={availableFields}
            forApproval={forApproval}
            allowLinking={allowLinking}
            inGroup={wrapInContext}
            onCopy={onCopy}
            onDelete={onDelete}
            onChange={onChange}
          />
        ))}
      </Accordion>
    );
    if (!wrapInContext) return inner;
    return (
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        {inner}
      </SortableContext>
    );
  };

  if (value.length === 0) return null;

  const groupKeys = Array.from(buckets.named.keys());

  // For grouped rows, also lock the drag overlay to the parent
  // (the bucket's row container) so the preview can't visually escape
  // its group. Top-level drags (groups, ungrouped rows) keep just the
  // vertical-axis restriction.
  const isActiveGroupedRow =
    activeId !== null &&
    activeId.startsWith("row:") &&
    bucketByIndex.get(Number(activeId.replace(/^row:/, ""))) !==
      "__ungrouped__";
  const dndModifiers = isActiveGroupedRow
    ? [restrictToVerticalAxis, restrictToParentElement]
    : [restrictToVerticalAxis];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={dndModifiers}
    >
      <SortableContext
        items={topLevelIds}
        strategy={verticalListSortingStrategy}
      >
        <div className="flex flex-col gap-3">
          {topLevelIds.map((id) => {
            if (id.startsWith("group:")) {
              const name = id.replace(/^group:/, "");
              const rows = buckets.named.get(name) ?? [];
              return (
                <SortableGroupBucket
                  key={id}
                  groupKey={name}
                  rows={rows}
                  isOpen={!closedBuckets.has(name)}
                  onToggle={() => toggleBucket(name)}
                  onRemove={() => removeGroup(name)}
                  isDisabled={isDisabled}
                  renderRows={(r) => renderRowsList(r, true)}
                />
              );
            }
            // Ungrouped row — find it in buckets.other and render the
            // single AccordionField (still inside the top-level
            // SortableContext, which provides its useSortable scope).
            const idx = Number(id.replace(/^row:/, ""));
            const item = buckets.other.find((r) => r.index === idx);
            if (!item) return null;
            return renderRowsList([item], false);
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
};

interface SortableGroupBucketProps {
  groupKey: string;
  rows: RowWithIndex[];
  isOpen: boolean;
  onToggle: () => void;
  onRemove: () => void;
  isDisabled?: boolean;
  renderRows: (rows: RowWithIndex[]) => React.ReactNode;
}

const SortableGroupBucket = ({
  groupKey,
  rows,
  isOpen,
  onToggle,
  onRemove,
  isDisabled,
  renderRows,
}: SortableGroupBucketProps) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useSortable({ id: `group:${groupKey}`, disabled: isDisabled });
  const style = {
    transform: CSS.Transform.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border rounded-md bg-[#FCFCFC] overflow-hidden"
    >
      <div className="flex items-center gap-2 px-4 py-2.5 hover:bg-muted/30 transition-colors">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
        </div>
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left cursor-pointer"
        >
          <span className="text-sm font-medium">{groupKey}</span>
          <Badge variant="color-blue" isCompact>
            Linked
          </Badge>
          <span className="text-xs text-muted-foreground">
            {rows.length} propert{rows.length === 1 ? "y" : "ies"}
          </span>
        </button>
        <NoxusTooltip content="Remove group">
          <Button
            variant="ghost"
            size="iconsm"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            isDisabled={isDisabled}
          >
            <Trash className="size-4" strokeWidth={1.25} />
          </Button>
        </NoxusTooltip>
        <Button type="button" variant="ghost" size="iconsm" onClick={onToggle}>
          <ChevronUp
            className={cn(
              "transition-transform duration-200",
              !isOpen ? "rotate-180" : ""
            )}
          />
        </Button>
      </div>
      {isOpen && <div className="p-3 border-t">{renderRows(rows)}</div>}
    </div>
  );
};

interface ExtractionSchemaBuilderProps {
  title?: string;
  subtitle?: string;
  tooltip?: string;
  value: ExtractionField[];
  typeOptions: string[];
  availableFields?: AvailableFieldOption[];
  forApproval?: boolean;
  allowLinking?: boolean;
  onChange: (value: ExtractionField[]) => void;
  isDisabled?: boolean;
  /** Rendered inline at the end of the Add field / Import group row. */
  footerExtra?: React.ReactNode;
}

const ExtractionSchemaBuilderComponent = ({
  title,
  subtitle,
  tooltip,
  value,
  typeOptions,
  availableFields,
  forApproval,
  allowLinking,
  onChange,
  isDisabled,
  footerExtra,
}: ExtractionSchemaBuilderProps) => {
  const [openItems, setOpenItems] = useState<string[]>([]);

  // When linking is disabled, strip field_id / editable from rows so storage
  // matches the UI. Without this, stale link state lingers and re-activates
  // if the user toggles linking back on (and the runtime safeguard would
  // be the only thing preventing case.data writes).
  useEffect(() => {
    if (allowLinking) return;
    const needsClean = value.some(
      (r) => r.field_id !== undefined || r.editable !== undefined
    );
    if (!needsClean) return;
    onChange(value.map(({ field_id: _f, editable: _e, ...rest }) => rest));
  }, [allowLinking, value, onChange]);

  const cantDelete = value.length <= 1;

  // Duplicate detection now operates on Label (the user-typed canonical
  // identifier). Two entries with the same Label would land on the same key
  // in `task.output` and silently overwrite each other.
  const duplicateLabelIndices = useMemo(() => {
    const labels = value.map((e) => e.Label?.toLowerCase().trim() || "");
    const dupes = new Set<number>();
    const seen = new Map<string, number>();
    for (let i = 0; i < labels.length; i++) {
      if (!labels[i]) continue;
      if (seen.has(labels[i])) {
        dupes.add(i);
      } else {
        seen.set(labels[i], i);
      }
    }
    return dupes;
  }, [value]);

  const ensureUniqueLabel = useCallback(
    (newLabel: string, currentIndex: number) => {
      if (!newLabel) return newLabel;
      const existing = value
        .map((e, i) => ({
          label: e.Label?.toLowerCase().trim() || "",
          index: i,
        }))
        .filter((item) => item.index !== currentIndex && item.label);

      const lower = newLabel.toLowerCase().trim();
      if (!existing.some((item) => item.label === lower)) return newLabel;

      let counter = 2;
      let unique = `${newLabel} ${counter}`;
      while (
        existing.some((item) => item.label === unique.toLowerCase().trim())
      ) {
        counter++;
        unique = `${newLabel} ${counter}`;
      }
      return unique;
    },
    [value]
  );

  const handleAdd = useCallback(() => {
    if (isDisabled) return;
    // Infer the auto-increment prefix from existing rows so adding to a
    // list of "Source N" continues with "Source N+1" instead of switching
    // to "Field 1". Default to "Field" when no row matches `<Prefix> <N>`.
    const prefixCounts = new Map<string, number>();
    const maxForPrefix = new Map<string, number>();
    for (const item of value) {
      const match = item.Label?.match(/^(.+?) (\d+)$/);
      if (!match) continue;
      const [, prefix, n] = match;
      prefixCounts.set(prefix, (prefixCounts.get(prefix) || 0) + 1);
      const num = parseInt(n, 10);
      maxForPrefix.set(prefix, Math.max(maxForPrefix.get(prefix) ?? 0, num));
    }
    let prefix = "Field";
    let bestCount = 0;
    for (const [p, count] of prefixCounts) {
      if (count > bestCount) {
        prefix = p;
        bestCount = count;
      }
    }
    const next = (maxForPrefix.get(prefix) ?? 0) + 1;

    onChange([
      ...value,
      {
        id: crypto.randomUUID(),
        Label: `${prefix} ${next}`,
        type: "text",
      },
    ]);
  }, [value, onChange, isDisabled]);

  const handleDelete = useCallback(
    (index: number) => {
      if (isDisabled || cantDelete) return;
      onChange(value.filter((_, i) => i !== index));
    },
    [value, onChange, isDisabled, cantDelete]
  );

  const handleChange = useCallback(
    (index: number, fields: Partial<ExtractionField>) => {
      if (isDisabled) return;
      const updated = value.map((entry, i) => {
        if (i !== index) return entry;
        return { ...entry, ...fields };
      });
      onChange(updated);
    },
    [value, onChange, isDisabled]
  );

  const handleCopy = useCallback(
    (entry: ExtractionField) => {
      if (isDisabled) return;
      const newLabel = ensureUniqueLabel(entry.Label || "Field", -1);
      onChange([
        ...value,
        { ...entry, id: crypto.randomUUID(), Label: newLabel },
      ]);
    },
    [value, onChange, isDisabled, ensureUniqueLabel]
  );

  return (
    <div className="flex flex-col gap-2">
      <div>
        {title && <NoxusFormLabel label={title} tooltip={tooltip} />}
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>

      <SchemaBuckets
        value={value}
        onValueChange={onChange}
        availableFields={availableFields}
        forApproval={forApproval}
        allowLinking={allowLinking}
        openItems={openItems}
        setOpenItems={setOpenItems}
        isDisabled={isDisabled}
        cantDelete={cantDelete}
        duplicateLabelIndices={duplicateLabelIndices}
        typeOptions={typeOptions}
        onCopy={handleCopy}
        onDelete={handleDelete}
        onChange={handleChange}
      />

      <div className="flex gap-2 mt-2">
        <Button
          onClick={handleAdd}
          variant="outline"
          className="flex-1"
          disabled={isDisabled}
        >
          <Plus className="w-4 h-4" />
          Add field
        </Button>
        {allowLinking && availableFields && availableFields.length > 0 && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={isDisabled}
                className="flex-1"
              >
                <Plus className="w-4 h-4" />
                Import group
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-64"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const groups = Array.from(
                  new Set(
                    availableFields
                      .map((f) => f.group)
                      .filter((g): g is string => !!g)
                  )
                );
                if (groups.length === 0) {
                  return (
                    <DropdownMenuItem disabled>
                      No groups defined on this inbox
                    </DropdownMenuItem>
                  );
                }
                return groups.map((g) => {
                  const members = availableFields.filter((f) => f.group === g);
                  return (
                    <DropdownMenuItem
                      key={g}
                      onClick={() => {
                        const existingFieldIds = new Set(
                          value
                            .map((e) => e.field_id)
                            .filter((id): id is string => !!id)
                        );
                        const newRows: ExtractionField[] = members
                          .filter((m) => !existingFieldIds.has(m.field_id))
                          .map((m) => {
                            const mappedType = inboxTypeToExtractionType(
                              m.type
                            );
                            return {
                              id: crypto.randomUUID(),
                              Label: m.label,
                              type: mappedType,
                              field_id: m.field_id,
                              editable: true,
                              options:
                                mappedType === "select" ||
                                mappedType === "table"
                                  ? (m.options ?? undefined)
                                  : undefined,
                            };
                          });
                        if (newRows.length > 0)
                          onChange([...value, ...newRows]);
                      }}
                    >
                      <div className="flex flex-col items-start">
                        <span>{g}</span>
                        <span className="text-xs text-muted-foreground">
                          {members.length} propert
                          {members.length === 1 ? "y" : "ies"}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  );
                });
              })()}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {footerExtra}
      </div>
    </div>
  );
};

interface ExtractionSchemaBuilderConfigProps {
  name: string;
  label?: string;
  subtitle?: string;
  control: Control<any>;
  defaultValue?: ExtractionField[];
  tooltip?: string;
  typeOptions?: string[];
  availableFields?: AvailableFieldOption[];
  forApproval?: boolean;
  allowLinking?: boolean;
  isDisabled?: boolean;
  /** Sibling config key holding the optional task layout (rows + widths). */
  layoutName?: string;
  inheritedLayout?: unknown;
}

export const ExtractionSchemaBuilderConfig = ({
  name,
  label,
  subtitle,
  control,
  defaultValue,
  tooltip,
  typeOptions,
  availableFields,
  forApproval,
  allowLinking,
  isDisabled,
  layoutName,
  inheritedLayout,
}: ExtractionSchemaBuilderConfigProps) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field }) => (
        <ExtractionSchemaBuilderComponent
          title={label}
          subtitle={subtitle}
          tooltip={tooltip}
          value={field.value || []}
          typeOptions={typeOptions || TYPE_OPTIONS}
          availableFields={availableFields}
          forApproval={forApproval}
          allowLinking={allowLinking}
          onChange={field.onChange}
          isDisabled={isDisabled}
          footerExtra={
            layoutName ? (
              <Controller
                name={layoutName}
                control={control}
                defaultValue={{}}
                render={({ field: layoutField }) => (
                  <TaskLayoutDesigner
                    rows={(field.value || []) as ExtractionField[]}
                    value={layoutField.value}
                    onChange={layoutField.onChange}
                    onRowsChange={field.onChange}
                    inheritedLayout={inheritedLayout}
                    isDisabled={isDisabled}
                    iconOnly
                  />
                )}
              />
            ) : undefined
          }
        />
      )}
    />
  );
};
